/**
 * Progetti — una sezione sola.
 *
 * Prima erano tre voci di menu: Digital Marketing, Digital Product e Digital
 * Design. Marketing e Prodotto erano LETTERALMENTE lo stesso componente con
 * un'etichetta diversa, e Design apriva invece un elenco di consegne, che è
 * un'altra entità: un progetto di divisione Design non sarebbe comparso da
 * nessuna parte. Adesso la divisione è **un filtro**, non tre pagine.
 *
 * Cosa deve dire l'elenco a colpo d'occhio, in quest'ordine: per chi lo stiamo
 * facendo, che cos'è, a che punto è, quanto vale, quando scade e SE È IN
 * RITARDO. Il ritardo lo calcola il server e viaggia con la riga: la UI non
 * rifà i conti sulle date, che è il modo più semplice per dire "in ritardo" a
 * un progetto che non lo è perché il browser sta in un altro fuso.
 *
 * Il confine con le Trattative è il sì del cliente (os-planning/
 * trattativa-progetto.md): lì c'è quello che speriamo di vendere, qui quello
 * che dobbiamo consegnare. Appena nasce il progetto il suo valore esce dal
 * potenziale commerciale ed entra qui, in "valore impegnato": lo stesso euro
 * non compare in due posti nello stesso momento.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { useUrlState } from '../../hooks/useUrlState';
import { useKanbanDnd } from '../../lib/dnd';
import { Icon } from '../../components/ui/Icon';
import { Chip, statusTone } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { Avatar } from '../../components/ui/Avatar';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonKPIRow, SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { fmtEur, fmtDate } from '../../lib/utils';
import { Division, DIVISION_LABEL, ProjectStatus } from '@masamune/shared';
import {
  type ProjectRow,
  type ProjectFilters,
  type DeliverableRow,
  type FiltroConsegna,
  type OrdineProgetti,
  quantiFiltri,
  canWriteProjects,
  canReadDeliverables,
  projectsListQuery,
  projectsBoardQuery,
  projectsSummaryQuery,
  projectsDivisionsQuery,
  deliverablesQuery,
  projectClientsQuery,
  projectPmsQuery,
} from './projectsApi';
import './projects.css';

/** Le colonne della bacheca. CHIUSO non c'è: finito il lavoro, esce di scena. */
const COLONNE: ProjectStatus[] = [
  ProjectStatus.PREVENTIVO,
  ProjectStatus.ATTIVO,
  ProjectStatus.IN_CORSO,
  ProjectStatus.REVISIONE,
  ProjectStatus.IN_PAUSA,
];

const STATI: ProjectStatus[] = [...COLONNE, ProjectStatus.CHIUSO];

/**
 * Le divisioni che si vedono sempre come filtro: sono le tre che prima erano
 * tre pagine separate, e devono restare raggiungibili anche quando sono vuote,
 * altrimenti sembra che siano sparite. Talent e Amministrazione compaiono solo
 * se hanno davvero dei progetti: hanno già una loro sezione e qui sarebbero due
 * pill sempre a zero.
 */
const DIVISIONI_FISSE: Division[] = [Division.MKT, Division.PROD, Division.DESIGN];
const DIVISIONI_EXTRA: Division[] = [Division.TALENT, Division.ADMIN];

type Vista = 'lista' | 'bacheca' | 'consegne';

/**
 * Il valore che l'URL usa per dire "nessuna divisione". Non è '' perché sui
 * vecchi indirizzi `/divisions/*` la stringa vuota fa sparire il parametro e
 * quindi riporta alla divisione di partenza: vedi il commento nella pagina.
 */
const TUTTE = 'tutte';

const STATUS_LABEL = (s: string) => s.replace(/_/g, ' ').toLowerCase();

/** "1 progetto" / "4 progetti": il singolare sbagliato si nota, ed è sciatto. */
const plurale = (n: number, uno: string, molti: string) => `${n} ${n === 1 ? uno : molti}`;

/**
 * Ordina per (24/9), solo nella vista Lista: lo fa l'API, perché la lista è
 * paginata. Il primo è l'ordine di sempre. Negli altri i progetti chiusi
 * restano comunque in fondo: l'elenco serve a sapere cosa consegnare.
 */
const ORDINI_PROGETTI: OpzioneOrdina[] = [
  { key: 'stato', label: 'Per stato, poi consegna più vicina' },
  { key: 'consegna', label: 'Consegna più vicina prima' },
  { key: 'valore', label: 'Prima i progetti che valgono di più' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
  { key: 'inizio', label: 'Iniziati più di recente' },
];

/** Filtra per: le voci dicono di cosa parlano anche lette da sole. */
const FILTRO_STATO: Record<ProjectStatus, string> = {
  [ProjectStatus.PREVENTIVO]: 'Solo i progetti ancora in preventivo',
  [ProjectStatus.ATTIVO]: 'Solo i progetti attivi',
  [ProjectStatus.IN_CORSO]: 'Solo i progetti in corso',
  [ProjectStatus.REVISIONE]: 'Solo i progetti in revisione',
  [ProjectStatus.IN_PAUSA]: 'Solo i progetti in pausa',
  [ProjectStatus.CHIUSO]: 'Solo i progetti chiusi',
};

const FILTRO_CONSEGNA: Array<{ value: Exclude<FiltroConsegna, ''>; label: string }> = [
  { value: 'in-ritardo', label: 'Solo i progetti in ritardo' },
  { value: 'entro-30-giorni', label: 'Solo i progetti da consegnare entro 30 giorni' },
  { value: 'senza-data', label: 'Solo i progetti senza data di consegna' },
];
const CONSEGNE_VALIDE = new Set<string>(FILTRO_CONSEGNA.map((o) => o.value));

export function ProjectsPage({ initialDivision, initialVista }: { initialDivision?: Division; initialVista?: Vista } = {}) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const canWrite = canWriteProjects(user);
  // Le consegne stanno dietro il modulo DESIGN lato API: chi non ce l'ha non
  // deve nemmeno vedere la linguetta, altrimenti clicca e trova una vista che
  // non gli dice la verità.
  const vedeConsegne = canReadDeliverables(user);
  const showMargin = !!user?.role.canSeeMargin;

  // Vista e divisione stanno nell'URL: tornando dal dettaglio si ritrova quello
  // che si stava guardando, e un elenco filtrato si incolla a qualcuno.
  const [vista, setVista] = useUrlState<Vista>('vista', initialVista ?? 'lista');
  /**
   * La divisione, con un'attenzione che serve sui vecchi indirizzi.
   *
   * `useUrlState` toglie il parametro dall'URL quando vale '' *oppure* quanto
   * il suo default, e su `/divisions/marketing` il default È la divisione:
   * "Tutte le divisioni" scriveva '', il parametro spariva, il valore tornava
   * a essere il default, e la pill non faceva niente. Era un tasto morto
   * proprio sui due indirizzi da cui oggi si entra nella sezione.
   *
   * Quindi "tutte" si scrive per esteso invece di cancellare, e si traduce
   * qui: l'URL resta leggibile (`?divisione=tutte`) e resta incollabile.
   */
  const [divisioneParam, setDivisioneParam] = useUrlState<string>('divisione', initialDivision ?? '');
  const divisione = divisioneParam === TUTTE ? '' : divisioneParam;
  const setDivisione = (d: string) => setDivisioneParam(d === '' && initialDivision ? TUTTE : d);
  const [clientId, setClientId] = useUrlState<string>('cliente', '');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  /**
   * Stato, consegna e chi lo segue stanno in "Filtra per" (24/9), con le
   * stesse chiavi di prima nell'URL (`stato`, `pm`). La consegna è nuova
   * (`consegna=in-ritardo|entro-30-giorni|senza-data`) e prende il posto di
   * `ritardo=1`, che si traduce una volta sola qui sotto: i link salvati
   * continuano ad aprire la lista giusta.
   */
  const [params, setParams] = useSearchParams();
  const statoParam = params.get('stato') ?? '';
  const pmId = params.get('pm') ?? '';
  const ritardoVecchio = params.get('ritardo') === '1';
  const consegnaParam = params.get('consegna') ?? '';
  const consegna = (CONSEGNE_VALIDE.has(consegnaParam) ? consegnaParam : ritardoVecchio ? 'in-ritardo' : '') as FiltroConsegna;
  useEffect(() => {
    if (!ritardoVecchio) return;
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('ritardo');
      if (!sp.get('consegna')) sp.set('consegna', 'in-ritardo');
      return sp;
    }, { replace: true });
  }, [ritardoVecchio, setParams]);
  // Nella bacheca lo stato non filtra: le colonne SONO gli stati.
  const status = vista === 'lista' && (STATI as string[]).includes(statoParam) ? statoParam : '';
  const ordina = useOrdina(ORDINI_PROGETTI);
  const [page, setPage] = useState(1);

  const filtri: ProjectFilters = { division: divisione, status, clientId, pmId, search, consegna };
  const attivi = quantiFiltri(filtri);
  const haFiltri = attivi > 0 || !!divisione;

  // Cambiato un filtro o l'ordine, si riparte da pagina 1: restare a pagina 3
  // su un insieme che adesso ha due righe vuol dire guardare una pagina vuota.
  useEffect(() => { setPage(1); }, [divisione, status, clientId, pmId, search, consegna, vista, ordina.key]);

  const summaryQ = useQuery(projectsSummaryQuery(filtri));
  const divisionsQ = useQuery(projectsDivisionsQuery(filtri));
  const listaQ = useQuery({ ...projectsListQuery(filtri, page, ordina.key as OrdineProgetti), enabled: vista === 'lista' });
  const boardQ = useQuery({ ...projectsBoardQuery(filtri), enabled: vista === 'bacheca' });
  const consegneQ = useQuery({ ...deliverablesQuery, enabled: vista === 'consegne' && vedeConsegne });
  const clientiQ = useQuery(projectClientsQuery);
  const pmQ = useQuery(projectPmsQuery);

  const summary = summaryQ.data;

  /**
   * I gruppi di "Filtra per". Lo stato solo nella Lista (nella bacheca sono le
   * colonne). Accanto alle voci della consegna i numeri della fascia in testa,
   * che il server conta sullo stesso filtro; quando una consegna è già scelta
   * i numeri si tolgono, perché conterebbero dentro quella scelta e
   * spegnerebbero le altre voci.
   */
  const pms = pmQ.data?.items;
  const defsFiltri = useMemo<FiltroDef[]>(() => {
    const conti: Record<string, number | undefined> = !consegna && summary
      ? { 'in-ritardo': summary.inRitardo, 'entro-30-giorni': summary.inConsegna30, 'senza-data': summary.senzaScadenza }
      : {};
    return [
      ...(vista === 'lista'
        ? [{ param: 'stato', label: 'A che punto è', opzioni: STATI.map((s) => ({ value: s, label: FILTRO_STATO[s] })) }]
        : []),
      { param: 'consegna', label: 'Consegna', opzioni: FILTRO_CONSEGNA.map((o) => ({ ...o, count: conti[o.value] })) },
      ...(pms?.length
        ? [{ param: 'pm', label: 'Chi lo segue', opzioni: pms.map((u) => ({ value: u.id, label: `Seguiti da ${u.firstName} ${u.lastName}` })) }]
        : []),
    ];
  }, [vista, consegna, summary, pms]);
  const filtriUi = useFiltri(defsFiltri);
  const toggleConsegna = (v: Exclude<FiltroConsegna, ''>) => filtriUi.set('consegna', consegna === v ? [] : [v]);
  /**
   * La vista "Consegne" mostra un'altra entità: i Deliverable, non i progetti.
   * I numeri in testa e il filtro per divisione parlano di progetti, quindi lì
   * NON si mostrano: lasciarli vorrebbe dire mettere "1 progetto in ritardo"
   * sopra una tabella di consegne che non c'entra, e chi guarda li legge
   * insieme. Stessa ragione per cliente, PM e stato.
   */
  const isConsegne = vista === 'consegne';
  const contaDivisione = (d: Division) => divisionsQ.data?.items.find((i) => i.division === d)?.count ?? 0;

  // Le consegne si filtrano qui: sono duecento righe al massimo, e la ricerca
  // deve rispondere mentre si digita senza rifare il giro del server.
  const consegneVisibili = (consegneQ.data?.items ?? []).filter((d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${d.title} ${d.project?.name ?? ''} ${d.owner ? `${d.owner.firstName} ${d.owner.lastName}` : ''}`.toLowerCase().includes(q);
  });
  const divisioniVisibili = [
    ...DIVISIONI_FISSE,
    ...DIVISIONI_EXTRA.filter((d) => contaDivisione(d) > 0 || divisione === d),
  ];

  /**
   * Un giro solo sull'URL: sei `set` di fila leggerebbero tutti lo stesso URL
   * di partenza, e resterebbe acceso tutto tranne l'ultimo. Ordine e vista
   * restano: non sono filtri.
   */
  const azzera = () => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const k of ['stato', 'cliente', 'pm', 'q', 'consegna', 'ritardo']) sp.delete(k);
      if (initialDivision) sp.set('divisione', TUTTE);
      else sp.delete('divisione');
      return sp;
    });
  };

  const apri = (id: string) => navigate(`/projects/${id}`);
  const nuovo = () => openModal('newProject', divisione ? { division: divisione } : undefined);

  return (
    <div className="page">
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Progetti</h1>
          <div className="ed-sub">Cosa stiamo consegnando, per chi, a che punto è e quando scade.</div>
        </div>
        {/* Sul telefono l'azione principale non sta in cima, dove il pollice non
            arriva: sta nella barra in fondo. */}
        <div className="flex gap-2 pb-1 items-center d-only">
          {canWrite && (
            <button className="btn btn-primary" onClick={nuovo}>
              <Icon name="plus" /> Nuovo progetto
            </button>
          )}
        </div>
      </div>

      {isConsegne ? null : summaryQ.isLoading && !summary ? (
        <SkeletonKPIRow count={4} withIcon={false} />
      ) : (
        <div className="stat-band">
          <div>
            <div className="st-label">Progetti aperti</div>
            <div className="st-value"><AnimatedNumber value={summary?.vivi ?? 0} /></div>
            <div className="st-delta">{summary?.totale ?? 0} in tutto</div>
          </div>
          <div>
            <div className="st-label">Valore impegnato</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={summary?.valoreImpegnato ?? 0} format={fmtEur} /></div>
            {/* Il promemoria della regola: questo denaro è già uscito dal
                potenziale commerciale, non va sommato di nuovo. */}
            <div className="st-delta">fuori dal potenziale</div>
          </div>
          {/* Tutti e due i numeri sono anche il loro filtro: si clicca e la
              lista si stringe (è la stessa voce di "Filtra per", Consegna). */}
          <div
            className="st-click"
            role="button"
            tabIndex={0}
            aria-pressed={consegna === 'in-ritardo'}
            onClick={() => toggleConsegna('in-ritardo')}
            onKeyDown={(e) => { if (e.key === 'Enter') toggleConsegna('in-ritardo'); }}
          >
            <div className="st-label">In ritardo</div>
            <div className="st-value"><AnimatedNumber value={summary?.inRitardo ?? 0} /></div>
            <div className={`st-delta${(summary?.inRitardo ?? 0) > 0 ? ' text-alert' : ''}`}>
              {consegna === 'in-ritardo' ? 'Filtro attivo' : (summary?.inRitardo ?? 0) > 0 ? 'consegna sforata' : 'nessuno'}
            </div>
          </div>
          <div
            className="st-click"
            role="button"
            tabIndex={0}
            aria-pressed={consegna === 'entro-30-giorni'}
            onClick={() => toggleConsegna('entro-30-giorni')}
            onKeyDown={(e) => { if (e.key === 'Enter') toggleConsegna('entro-30-giorni'); }}
          >
            <div className="st-label">In consegna entro 30 giorni</div>
            <div className="st-value"><AnimatedNumber value={summary?.inConsegna30 ?? 0} /></div>
            {consegna === 'entro-30-giorni' && <div className="st-delta">Filtro attivo</div>}
          </div>
        </div>
      )}

      {/* Quello che resta fuori dai conti, detto in una riga sola: un progetto
          senza data non è in ritardo e non è in scadenza, semplicemente non si
          sa quando consegna. Si dice, non si nasconde. */}
      {!isConsegne && !!summary?.senzaScadenza && (
        <div className="adm-note">
          <Icon name="help" size={12} className="mute" />
          <span>
            <strong>{summary.senzaScadenza}</strong>{' '}
            {summary.senzaScadenza === 1 ? 'progetto non ha' : 'progetti non hanno'} una data di consegna:
            {' '}non {summary.senzaScadenza === 1 ? 'è' : 'sono'} in ritardo, semplicemente non si sa quando {summary.senzaScadenza === 1 ? 'chiude' : 'chiudono'}.
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => toggleConsegna('senza-data')}>
            {consegna === 'senza-data' ? 'Torna a tutti i progetti' : summary.senzaScadenza === 1 ? 'Mostra solo questo' : 'Mostra solo questi'}
          </button>
        </div>
      )}

      {/* La divisione: era tre pagine, adesso è questa riga. Il conteggio serve
          a non far cliccare a vuoto su una divisione vuota. */}
      {!isConsegne && (
      <div className="page-tabs">
        <div className="page-tab" aria-selected={!divisione} onClick={() => setDivisione('')}>
          Tutte le divisioni
          <span className="c">{divisionsQ.data?.totale ?? 0}</span>
        </div>
        {divisioniVisibili.map((d) => (
          <div key={d} className="page-tab" aria-selected={divisione === d} onClick={() => setDivisione(d)}>
            {DIVISION_LABEL[d]}
            <span className="c">{contaDivisione(d)}</span>
          </div>
        ))}
      </div>
      )}

      <MobileFilters attivi={isConsegne ? (search.trim() ? 1 : 0) : attivi} label={isConsegne ? 'Cerca' : 'Cerca, ordina e filtra'}>
        <div className="filterbar filterbar--float mb-4 flex-wrap">
          <div className="tweak-seg" role="group" aria-label="Vista">
            <button aria-pressed={vista === 'lista'} onClick={() => setVista('lista')}><Icon name="list" size={12} /> Lista</button>
            <button aria-pressed={vista === 'bacheca'} onClick={() => setVista('bacheca')}><Icon name="kanban" size={12} /> Bacheca</button>
            {vedeConsegne && (
              <button aria-pressed={vista === 'consegne'} onClick={() => setVista('consegne')}><Icon name="palette" size={12} /> Consegne</button>
            )}
          </div>

          <div className="divider" />

          <div className="tb-search" style={{ flex: '1 1 150px', minWidth: 150, maxWidth: 300, margin: 0 }}>
            <Icon name="search" size={12} />
            <input
              aria-label={isConsegne ? 'Cerca consegne' : 'Cerca progetti'}
              placeholder={isConsegne ? 'Cerca una consegna…' : 'Cerca per cliente, nome o codice…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Cliente, chi lo segue, stato e consegna sono attributi del
              PROGETTO: nella vista Consegne non filtrerebbero niente, e una
              tendina che non fa niente è peggio di una tendina che non c'è. */}
          {/* inp-sm invece di `height: 32` scritto nello style: e' lo stesso
              pixel (--h-base), ma detto con una classe. L'altezza inline batteva
              anche mobile.css, e sul telefono questi menu restavano alti 32
              invece dei 44 minimi. */}
          {/* Il cliente resta una tendina: sono duecento nomi, e la tendina
              del sistema li scorre scrivendo le iniziali. Chi lo segue, stato
              e consegna sono pochi e stanno in "Filtra per". */}
          {!isConsegne && (
            <select className="inp inp-sm" style={{ width: 160 }} value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Cliente">
              <option value="">Tutti i clienti</option>
              {(clientiQ.data?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.shortName || c.legalName}</option>)}
            </select>
          )}

          {/* ml-auto e non uno .spacer: la barra va a capo, e uno spacer con
              flex-basis 0 resta appeso alla riga di sopra lasciando il
              conteggio incolonnato a sinistra sotto il primo comando, come
              un'etichetta persa. Il margine automatico invece spinge a destra
              la riga su cui il conteggio finisce davvero. */}
          <span className="mute text-[12px] ml-auto">
            {isConsegne
              // Senza il permesso non si conta niente, quindi non si scrive un
              // numero: "0 consegne" sarebbe un dato, e il dato non ce l'abbiamo.
              ? (vedeConsegne ? plurale(consegneVisibili.length, 'consegna', 'consegne') : '')
              : plurale((vista === 'lista' ? listaQ.data?.total : boardQ.data?.total) ?? 0, 'progetto', 'progetti')}
          </span>

          {/* Ordina solo nella Lista: nella bacheca l'ordine delle schede lo
              decide chi le trascina. */}
          {!isConsegne && (
            <OrdinaFiltra ordina={vista === 'lista' ? ordina : undefined} filtri={filtriUi} />
          )}
        </div>
      </MobileFilters>

      {vista === 'lista' && (
        <ListaProgetti
          query={listaQ}
          page={page}
          onPage={setPage}
          showMargin={showMargin}
          haFiltri={haFiltri}
          onAzzera={azzera}
          canWrite={canWrite}
          onNuovo={nuovo}
          onApri={apri}
        />
      )}

      {vista === 'bacheca' && (
        // Colonne da guardare una accanto all'altra e schede che si spostano
        // trascinandole: su 390px non è una vista, è un imbuto.
        <DesktopOnly
          cosa="La bacheca"
          perche="sono colonne da guardare una accanto all'altra, e i progetti si spostano trascinandoli. Dal telefono resta la lista, che dice le stesse cose."
        >
          <BachecaProgetti
            items={boardQ.data?.items ?? []}
            filtri={filtri}
            isLoading={boardQ.isLoading && !boardQ.data}
            haFiltri={haFiltri}
            onAzzera={azzera}
            onApri={apri}
          />
        </DesktopOnly>
      )}

      {/* Ci si arriva solo con un vecchio indirizzo (`/divisions/design`) o con
          un link incollato: la linguetta, a chi non può vederle, non c'è. Si
          dice com'è, invece di mostrare un elenco vuoto che sembra un dato. */}
      {vista === 'consegne' && !vedeConsegne && (
        <div className="card">
          <EmptyState
            icon="lock"
            title="Le consegne non sono visibili con il tuo profilo"
            description="Servono i permessi della divisione Design. I progetti, quelli sì: sono nella vista Lista."
            action={<button className="btn btn-sm" onClick={() => setVista('lista')}>Vai alla lista</button>}
          />
        </div>
      )}

      {vista === 'consegne' && vedeConsegne && (
        <ConsegneDesign
          items={consegneVisibili}
          isLoading={consegneQ.isLoading && !consegneQ.data}
          haRicerca={!!search.trim()}
          onApriProgetto={apri}
        />
      )}

      {canWrite && (
        <DesktopOnly
          cosa="L'apertura di un nuovo progetto"
          perche="richiede cliente, offerta, scadenze e responsabilità; dal telefono puoi consultare il lavoro e aggiungere task."
        >
          <div className="m-actionbar m-only">
            <button className="btn btn-primary" onClick={nuovo}>
              <Icon name="plus" /> Nuovo progetto
            </button>
          </div>
        </DesktopOnly>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// La lista
// ───────────────────────────────────────────────────────────────────────────

function ListaProgetti({
  query, page, onPage, showMargin, haFiltri, onAzzera, canWrite, onNuovo, onApri,
}: {
  query: { data?: { items: ProjectRow[]; total: number; totalPages: number }; isLoading: boolean };
  page: number;
  onPage: (p: number) => void;
  showMargin: boolean;
  haFiltri: boolean;
  onAzzera: () => void;
  canWrite: boolean;
  onNuovo: () => void;
  onApri: (id: string) => void;
}) {
  if (query.isLoading && !query.data) {
    return (
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card"><tbody><SkeletonTable rows={6} cols={6} withAvatar={false} /></tbody></table>
      </div>
    );
  }

  const items = query.data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="cube"
          title={haFiltri ? 'Nessun progetto con questi filtri' : 'Non c’è ancora nessun progetto'}
          description={
            haFiltri
              ? 'Prova ad allargare la ricerca, oppure azzera i filtri.'
              : 'I progetti nascono da una trattativa vinta, oppure si aprono da zero per i lavori che non passano da una trattativa.'
          }
          action={
            haFiltri
              ? <button className="btn btn-sm" onClick={onAzzera}><Icon name="x" size={10} /> Azzera i filtri</button>
              : canWrite
                ? <button className="btn btn-sm btn-primary d-only" onClick={onNuovo}><Icon name="plus" size={10} /> Apri il primo progetto</button>
                : undefined
          }
        />
      </div>
    );
  }

  return (
    <>
      {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
          non cambia niente: data-m e data-l sono attributi, non stili. */}
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card">
          <thead>
            <tr>
              <th>Progetto</th>
              <th>Divisione</th>
              <th>A che punto è</th>
              <th className="num">Valore</th>
              <th className="num">Consegna</th>
              <th>Chi lo segue</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <RigaProgetto key={p.id} p={p} showMargin={showMargin} onApri={onApri} />
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={query.data?.items.length ?? 25}
        total={query.data?.total ?? 0}
        totalPages={query.data?.totalPages ?? 1}
        onPage={onPage}
      />
    </>
  );
}

function RigaProgetto({ p, showMargin, onApri }: { p: ProjectRow; showMargin: boolean; onApri: (id: string) => void }) {
  const navigate = useNavigate();
  return (
    <tr onClick={() => onApri(p.id)} title={p.name} className={p.isLate ? 'is-late' : undefined}>
      {/* Il cliente è il modo in cui un progetto si chiama davvero ("il sito
          della Rosa"): sta sopra. Il nome del lavoro è la seconda riga. */}
      <td data-m="hd">
        <div className="font-medium">{p.client.shortName || p.client.legalName}</div>
        <div className="text-[12px] mute truncate" style={{ maxWidth: 280 }}>{p.name}</div>
      </td>

      {/* Niente text-[13px] sui <td>: `.tbl tbody td` fissa il corpo a 14px con
          due elementi nel selettore, l'utility ne ha uno e perde. Era codice
          che diceva una misura mai andata a schermo. */}
      <td data-m="key" data-l="Divisione">{DIVISION_LABEL[p.division]}</td>

      {/* La frase intera nel title: `.prj-fase` la taglia coi puntini da
          computer (vedi projects.css), e senza il title il pezzo che manca non
          si potrebbe leggere da nessuna parte. */}
      <td data-m="key" data-l="A che punto è" style={{ whiteSpace: 'nowrap' }} title={p.phase || STATUS_LABEL(p.status)}>
        <Chip tone={statusTone(p.status)} dot>
          <span className="prj-fase">{p.phase || STATUS_LABEL(p.status)}</span>
        </Chip>
      </td>

      {/* Il margine si mostra solo se i costi ci sono davvero. Con `cost` a
          zero il conto darebbe "margine 100%" su ogni riga: non è un margine
          eccellente, è un costo che nessuno ha ancora scritto, e scritto così
          verrebbe letto come un dato vero. */}
      <td data-m="key" data-l="Valore" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>
        {/* Stessa regola dei costi: "€ 0" si legge come "questo lavoro non vale
            niente", mentre di solito vuol dire che nessun documento dice quanto
            vale (Graci, che si fattura a mese senza preventivo). Un trattino
            dice "non lo sappiamo"; il perché sta nelle note della scheda. */}
        {p.revenue > 0 ? <>€ {fmtEur(p.revenue)}</> : <span className="mute">—</span>}
        {showMargin && p.marginPct != null && (p.cost ?? 0) > 0 && (
          <div className={`text-[11px] font-normal ${p.marginPct < 20 ? 'text-alert' : 'mute'}`}>
            margine {p.marginPct.toFixed(0)}%
          </div>
        )}
        {showMargin && (p.cost ?? 0) === 0 && (
          <div className="text-[11px] font-normal mute-2">costi da inserire</div>
        )}
      </td>

      {/* La scadenza e il ritardo sono la stessa domanda: stanno nella stessa
          cella, così non serve una colonna in più solo per un pallino. */}
      <td data-m="key" data-l="Consegna" className="num mono" style={{ whiteSpace: 'nowrap' }}>
        {p.deadline ? fmtDate(p.deadline) : <span className="mute-2">da fissare</span>}
        {p.isLate && (
          <div className="text-[11px] text-alert">
            {p.giorniRitardo === 1 ? '1 giorno di ritardo' : `${p.giorniRitardo} giorni di ritardo`}
          </div>
        )}
        {!p.isLate && p.oltrePromessa && (
          <div className="text-[11px] mute" title="La consegna che ci siamo dati è più in là della data promessa in trattativa">
            oltre la promessa
          </div>
        )}
      </td>

      <td data-m="off">
        {p.pm ? <Avatar initials={p.pm.initials} size={22} name={`${p.pm.firstName} ${p.pm.lastName}`} /> : <span className="mute-2">—</span>}
      </td>

      <td data-m="act" onClick={(e) => e.stopPropagation()}>
        <ActionsMenu
          variant="ghost"
          label={`Azioni su ${p.name}`}
          items={[
            { key: 'apri', label: 'Apri il progetto', icon: 'arrowRight', onClick: () => onApri(p.id) },
            ...(p.deal
              // navigate, non window.location: ricaricare tutta l'applicazione
              // per cambiare pagina butta via la cache e fa ripartire il login.
              ? [{ key: 'deal', label: 'Vai alla trattativa', icon: 'handshake' as const, onClick: () => navigate(`/trattative/${p.deal!.id}`) }]
              : []),
          ]}
        />
      </td>
    </tr>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// La bacheca
// ───────────────────────────────────────────────────────────────────────────

function BachecaProgetti({
  items, filtri, isLoading, haFiltri, onAzzera, onApri,
}: {
  items: ProjectRow[];
  filtri: ProjectFilters;
  isLoading: boolean;
  haFiltri: boolean;
  onAzzera: () => void;
  onApri: (id: string) => void;
}) {
  const qc = useQueryClient();
  const chiave = projectsBoardQuery(filtri).queryKey;

  const sposta = useMutation({
    mutationFn: ({ id, status, position }: { id: string; status: ProjectStatus; position: number }) =>
      api.post(`/projects/${id}/move`, { status, position }),
    // Ottimistico: la scheda si muove subito sotto il dito, non dopo il giro
    // di rete. Se il server rifiuta si rimette dov'era.
    onMutate: async ({ id, status, position }) => {
      await qc.cancelQueries({ queryKey: chiave });
      const prima = qc.getQueryData<{ items: ProjectRow[] }>(chiave);
      if (prima) {
        const mosso = prima.items.find((p) => p.id === id);
        if (mosso) {
          const altri = prima.items.filter((p) => p.id !== id);
          const stessaCol = altri.filter((p) => p.status === status);
          const altreCol = altri.filter((p) => p.status !== status);
          const pos = Math.max(0, Math.min(position, stessaCol.length));
          qc.setQueryData(chiave, {
            ...prima,
            items: [...altreCol, ...stessaCol.slice(0, pos), { ...mosso, status }, ...stessaCol.slice(pos)],
          });
        }
      }
      return { prima };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prima) qc.setQueryData(chiave, ctx.prima); },
    onSettled: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const dnd = useKanbanDnd<ProjectStatus>((id, status, position) => sposta.mutate({ id, status, position }));

  if (!isLoading && items.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="kanban"
          title={haFiltri ? 'Nessun progetto con questi filtri' : 'La bacheca è vuota'}
          description="Le colonne sono gli stati: un progetto si sposta trascinandolo."
          action={haFiltri ? <button className="btn btn-sm" onClick={onAzzera}><Icon name="x" size={10} /> Azzera i filtri</button> : undefined}
        />
      </div>
    );
  }

  return (
    // `kanban--fit` come nelle Trattative: la colonna si ferma all'altezza
    // dello schermo e scorre dentro di sé, invece di allungare la pagina in
    // proporzione al numero di progetti in quello stato.
    <div className="kanban kanban--fit">
      {COLONNE.map((s) => {
        const schede = items.filter((p) => p.status === s);
        const sopra = dnd.isOverColumn(s);
        return (
          <div className={`kanban-col${sopra ? ' is-over' : ''}`} key={s} {...dnd.columnProps(s)}>
            <div className="kanban-col-hd">
              <div className="kanban-col-title">
                <span className="dot" style={{ background: coloreStato(s) }} />
                {STATUS_LABEL(s)}
              </div>
              <span className="kanban-col-count">{schede.length}</span>
            </div>
            <div className="kanban-col-body">
              <ZonaDrop dnd={dnd} status={s} position={0} visibile={dnd.isAnyDragging()} />
              {schede.map((p, i) => (
                <Fragment key={p.id}>
                  <div
                    className={`kanban-card cursor-grab active:cursor-grabbing${p.isLate ? ' is-late' : ''}`}
                    {...dnd.cardProps(p.id, p.status)}
                    onClick={() => onApri(p.id)}
                  >
                    <div className="kanban-card-title">{p.client.shortName || p.client.legalName}</div>
                    <div className="mute text-[12px] mb-2 truncate">{p.name}</div>
                    <div className="row between">
                      <div className="av-stack">{p.pm && <Avatar initials={p.pm.initials} size={20} />}</div>
                      <span className={`mono text-[12px] ${p.isLate ? 'text-alert' : 'mute'}`}>
                        {p.deadline ? fmtDate(p.deadline) : '—'}
                      </span>
                    </div>
                  </div>
                  <ZonaDrop dnd={dnd} status={s} position={i + 1} visibile={dnd.isAnyDragging()} />
                </Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function coloreStato(s: ProjectStatus): string {
  if (s === ProjectStatus.IN_CORSO || s === ProjectStatus.ATTIVO) return 'var(--ok)';
  if (s === ProjectStatus.PREVENTIVO) return 'var(--accent)';
  if (s === ProjectStatus.REVISIONE) return 'var(--warn)';
  return 'var(--mute)';
}

function ZonaDrop<S extends string>({ dnd, status, position, visibile }: {
  dnd: ReturnType<typeof useKanbanDnd<S>>;
  status: S;
  position: number;
  visibile: boolean;
}) {
  const attiva = dnd.isOverZone(status, position);
  if (!visibile) return null;
  return <div {...dnd.dropZoneProps(status, position)} className={`kanban-dropzone${attiva ? ' is-over' : ''}`} />;
}

// ───────────────────────────────────────────────────────────────────────────
// Le consegne di Design
// ───────────────────────────────────────────────────────────────────────────

const TIPO_CONSEGNA: Record<string, string> = {
  DESIGN_SYSTEM: 'Design system',
  PROTOTYPE: 'Prototipo',
  BRAND_ASSET: 'Asset brand',
  SOCIAL_ASSET: 'Asset social',
  WEB_PAGE: 'Pagina web',
  PRESENTATION: 'Presentazione',
  OTHER: 'Altro',
};

const STATO_CONSEGNA: Record<string, string> = {
  BRIEF: 'Brief', DRAFT: 'Bozza', REVIEW: 'In revisione', APPROVED: 'Approvato', DELIVERED: 'Consegnato',
};

/**
 * Le consegne di Design non sono state buttate: sono **due cose in due posti**,
 * e il motivo è che il legame col progetto è facoltativo.
 *
 *  - dentro la scheda del progetto c'è la scheda "Consegne": sono il lavoro di
 *    quel progetto, e lì si vede a che punto è ognuna;
 *  - qui c'è la vista d'insieme, che le mostra TUTTE, comprese quelle che non
 *    sono agganciate a nessun progetto. Se le avessi messe solo dentro la
 *    scheda, le orfane sarebbero sparite dall'applicazione senza che nessuno
 *    se ne accorgesse.
 */
function ConsegneDesign({ items, isLoading, haRicerca, onApriProgetto }: {
  items: DeliverableRow[];
  isLoading: boolean;
  haRicerca: boolean;
  onApriProgetto: (id: string) => void;
}) {
  const senzaProgetto = items.filter((d) => !d.project).length;

  if (isLoading) {
    return (
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card"><tbody><SkeletonTable rows={5} cols={5} withAvatar={false} /></tbody></table>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="palette"
          title={haRicerca ? 'Nessuna consegna trovata' : 'Nessuna consegna'}
          description={
            haRicerca
              ? 'Prova con un’altra parola: la ricerca guarda il titolo, il progetto e chi la segue.'
              : 'Le consegne di Design (design system, prototipi, asset) si aprono dalla scheda del progetto a cui appartengono.'
          }
        />
      </div>
    );
  }

  return (
    <>
      {senzaProgetto > 0 && (
        <div className="adm-note">
          <Icon name="help" size={12} className="mute" />
          <span>
            <strong>{senzaProgetto}</strong> {senzaProgetto === 1 ? 'consegna non è agganciata' : 'consegne non sono agganciate'} a nessun progetto:
            {' '}{senzaProgetto === 1 ? 'si vede' : 'si vedono'} solo da qui.
          </span>
        </div>
      )}
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card">
          <thead>
            <tr>
              <th>Consegna</th><th>Progetto</th><th>Tipo</th><th>A che punto è</th><th className="num">Scadenza</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} onClick={() => d.project && onApriProgetto(d.project.id)} className={d.project ? undefined : 'is-orfana'}>
                <td data-m="hd">
                  <div className="font-medium">{d.title}</div>
                  {d.owner && <div className="text-[12px] mute">{d.owner.firstName} {d.owner.lastName}</div>}
                </td>
                {/* Stessa cosa qui: le utility di corpo sui <td> non arrivano
                    mai a schermo, le tolgo invece di lasciarle a mentire. */}
                <td data-m="key" data-l="Progetto">
                  {d.project
                    ? <span>{d.project.name}</span>
                    : <span className="mute-2">nessun progetto</span>}
                </td>
                <td data-m="key" data-l="Tipo">{TIPO_CONSEGNA[d.type] ?? d.type}</td>
                <td data-m="key" data-l="A che punto è">
                  <Chip tone={statusTone(d.status)} dot>{STATO_CONSEGNA[d.status] ?? d.status}</Chip>
                </td>
                <td data-m="key" data-l="Scadenza" className="num mono">{d.deadline ? fmtDate(d.deadline) : <span className="mute-2">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
